import { z } from 'zod';
import {
  CONDITION_VALUES,
  CONSTRUCTION_PHASE_VALUES,
  ENERGY_CERTIFICATE_BASIS_VALUES,
  ENERGY_CLASS_VALUES,
  INTERIOR_QUALITY_VALUES,
} from './properties.schemas';
import { PropertyMemoryEntrySchema } from './memory.schemas';
import { refersTo } from '../common/refers-to';
import { FACT_MODALITIES, FactProvenanceSchema } from './fact-provenance.schemas';

// The property dossier (IMM-500.2) — the read-side view of the facts layer: the
// authoritative Objekt fields plus every live fact, grouped per taxonomy key,
// each entry carrying provenance, confidence and scope. Multi-source
// disagreement is returned as a `conflict` set rather than resolved to a winner.

// Which consumer the dossier was composed for. Set by the presenter from the
// route's auth tier, NEVER accepted as request input — the whole default-deny
// guarantee is that a public leaf can only ever pass `EXPOSE`, so there is no
// parameter a caller can supply to widen its own view. Echoed on the response
// for the consumer's own logging.
export const DOSSIER_CONSUMERS = ['INTERNAL', 'EXPOSE'] as const;
export const DossierConsumerSchema = z.enum(DOSSIER_CONSUMERS);
export type DossierConsumer = z.infer<typeof DossierConsumerSchema>;

// Where a fact sits relative to the property itself — mirrors the backend
// `FactScope` taxonomy token. `DEAL_CONTEXT` facts are withheld from public
// consumers regardless of their key's `publicSafe` flag.
const DossierFactScopeSchema = z.enum(['PROPERTY_INTRINSIC', 'DEAL_CONTEXT']);

// The source modality a fact was derived from — mirrors the backend
// `FactModality` token. `IMAGE` is reserved for IMM-388's photo analyser;
// `NONE` is a fact not read from a medium.
const DossierFactModalitySchema = z.enum(FACT_MODALITIES);

// The human-confirmed Objekt field value for a key, when it maps to one and it
// is set. `value` is the field's real (loosely typed) value; `propertyFieldKey`
// names the Objekt field it was read from.
export const DossierAuthoritativeSchema = z.object({
  value: z.unknown(),
  propertyFieldKey: z.string(),
});

// One live fact for a key: its own provenance, confidence and the moment it was
// extracted. `value` is the raw JSON reading.
export const DossierAdvisoryEntrySchema = z.object({
  factId: z.string(),
  value: z.unknown(),
  unit: z.string().nullable(),
  modality: DossierFactModalitySchema,
  sourceId: z.string(),
  sourceCategory: z.string().nullable(),
  // The display name of the source document, beside the opaque `sourceId` so a
  // human can act on the provenance. Null in three cases: the source document was
  // deleted since extraction (a LEFT JOIN with no match), an agent's `DOCUMENT` finding carrying the agent's fixed source id, not a
  // document id, and the `EXPOSE`
  // projection, which must never carry a customer's internal filename. Operators
  // only ever read `INTERNAL`, so the ambiguity is unobservable to them.
  sourceName: z.string().nullable(),
  confidence: z.number(),
  extractedAt: z.string(),
  // Null on facts recorded before IMM-1385.
  occurrence: z.string().nullable(),
  provenance: FactProvenanceSchema.nullable(),
});

/**
 * Which fact keys are `enum`-typed, and the exact value set each one declares
 * (IMM-686). Declared here, in the contract, because it is the binding two
 * otherwise-unconnected sources have to agree on: the seeded fact taxonomy
 * (`0072_imm_1383_fact_key_catalog_seed_dont_squash.sql`, which decides which keys carry
 * `value_type = 'enum'`) and the German label maps a renderer looks a value up
 * in. Neither can see the other, so the totality tests BL-069 asks for check
 * each against THIS registry rather than against each other — a backend test
 * that the seeded enum rows are exactly these keys, and an app test that every
 * key and every value here has a label.
 *
 * The value tuples are the property contract's own, never re-typed: a value
 * added to `CONDITION_VALUES` lands here for free and fails the label test until
 * someone writes its German.
 */
export const DOSSIER_FACT_ENUM_VALUES = {
  'ZUS.OBJEKTZUSTAND': CONDITION_VALUES,
  'BAU.AUSSTATTUNGSQUALITAET': INTERIOR_QUALITY_VALUES,
  'GEB.BAUPHASE': CONSTRUCTION_PHASE_VALUES,
  'ENE.ENERGIEAUSWEIS.ausweisart': ENERGY_CERTIFICATE_BASIS_VALUES,
  'ENE.ENERGIEAUSWEIS.effizienzklasse': ENERGY_CLASS_VALUES,
} as const satisfies Record<string, ReadonlyArray<string>>;

export type DossierEnumFactKey = keyof typeof DOSSIER_FACT_ENUM_VALUES;

/** The Objekt-field-keyed memory entries find their enum labels through the entry's catalog ID. */
export const PROPERTY_FIELD_ENUM_FACT_KEYS = {
  condition: 'ZUS.OBJEKTZUSTAND',
  interiorQuality: 'BAU.AUSSTATTUNGSQUALITAET',
  constructionPhase: 'GEB.BAUPHASE',
  certificateBasis: 'ENE.ENERGIEAUSWEIS.ausweisart',
  energyClass: 'ENE.ENERGIEAUSWEIS.effizienzklasse',
} as const satisfies Record<string, DossierEnumFactKey>;

// The primitive shape a key's values carry — mirrors the backend `FactValueType`
// taxonomy token. Reaches the client because rendering a value is impossible
// without it: an `enum` reading is a raw English literal (`DEMAND`) that only a
// label map turns into „Bedarfsausweis", and a `date` is an ISO string. The
// backend has always known the type and dropped it here (IMM-686). Composite
// readings (`list`, `structure`, `document_reference`) print as JSON. The tuple
// lives here, not in `admin-fact-keys.schemas`, which already imports this file.
export const FACT_VALUE_TYPES = [
  'text',
  'number',
  'enum',
  'boolean',
  'date',
  'list',
  'structure',
  'document_reference',
] as const;
export const DossierFactValueTypeSchema = z.enum(FACT_VALUE_TYPES);

// How a `number` key is rendered, declared on the fact taxonomy rather than
// inferred from the key's name. `PLAIN_INTEGER` is `1968` for `GEB.BAUJAHR` — no
// grouping separator, because a grouped `1.904` for a year is exactly IMM-444 —
// and `GROUPED_DECIMAL` is the German-grouped `120` for `FLA.WOHNFLAECHE`. `unit` is
// the canonical unit for the key (`m²`), null when the number carries none.
export const DOSSIER_NUMBER_STYLES = ['PLAIN_INTEGER', 'GROUPED_DECIMAL'] as const;
export const DossierNumberFormatSchema = z.object({
  style: z.enum(DOSSIER_NUMBER_STYLES),
  unit: z.string().nullable(),
});

// One dossier entry — a taxonomy key with its two-tier value: the authoritative
// Objekt field (or null) and every live fact (newest first). `conflict` is true
// when the live facts hold more than one distinct value; it is never resolved.
export const DossierEntrySchema = z.object({
  factKey: z.string(),
  label: z.string(),
  scope: DossierFactScopeSchema,
  publicSafe: z.boolean(),
  // The taxonomy's declared shape and number rendering for this key, carried so a
  // consumer can render the value in its own locale instead of printing the raw
  // reading. `numberFormat` is null for every non-`number` key, and for a `number`
  // key whose taxonomy row declares none — which renders as a plain integer with
  // no unit, the safe default (IMM-686).
  valueType: DossierFactValueTypeSchema,
  numberFormat: DossierNumberFormatSchema.nullable(),
  authoritative: DossierAuthoritativeSchema.nullable(),
  advisory: z.array(DossierAdvisoryEntrySchema),
  conflict: z.boolean(),
});

// The cached natural-language brief for a projection (IMM-500.3): prose about the
// property, generated from `entries` only — never from `memory`: one brief is
// cached per projection and served to every caller of it, so it cannot honour
// per-caller source access (IMM-1050.4). Kept fresh reactively. `generatedAt` is the moment it was produced
// and `model` the model that produced it. The brief never quotes confidence
// numbers or source ids — that is the structured entries' job.
export const DossierBriefSchema = z.object({
  text: z.string(),
  generatedAt: z.string(),
  model: z.string(),
  // Whether the cached prose still describes the entries beside it. The read path
  // already compares the stored `factsHash` against the live entries to decide
  // whether to queue a regeneration; this surfaces that same result so a consumer
  // can flag prose that has not caught up yet rather than presenting it as
  // current. The stale brief text is still returned — the flag reports the lag,
  // it does not suppress the prose. Lives on the brief, not the response: a null
  // brief has no staleness question to answer.
  stale: z.boolean(),
});

// One memory entry as the dossier carries it (IMM-1050.4): the memory's own entry
// plus `effectiveValue` — the member's correction when there is one, else the
// read value. The dossier's rule is "use the corrected value"; a consumer reads
// `effectiveValue` and never re-derives it.
export const DossierMemoryEntrySchema = PropertyMemoryEntrySchema.extend({ effectiveValue: z.unknown() });

// The entries that speak about the same thing — one Objekt field, or one label —
// newest first. `conflict` is true when their effective values disagree; like a
// fact key's conflict it is never resolved to a winner.
export const DossierMemoryGroupSchema = z.object({
  label: z.string(),
  fieldKey: z.string().nullable(),
  entries: z.array(DossierMemoryEntrySchema),
  conflict: z.boolean(),
});

export const PropertyDossierSchema = z.object({
  propertyId: refersTo('property', z.string()),
  consumer: DossierConsumerSchema,
  entries: z.array(DossierEntrySchema),
  /**
   * True when at least one entry is backed by an extracted fact, rather than
   * being an authoritative-only projection of an Objekt field. The composer
   * always emits the confirmed fields (IMM-500.2 AC 5), so an empty `entries`
   * array is not a signal any consumer can use — this is (IMM-654).
   */
  factsDetected: z.boolean(),
  // The cached brief for this projection, or `null` when none has been generated
  // yet (a normal state, not an error — AC 5). Freshness is the brief subscriber's
  // job (IMM-500.3), never the reader's.
  brief: DossierBriefSchema.nullable(),
  // Everything else immoOS has read about the Objekt (IMM-1050), grouped. Only
  // what the caller may see of each source; always empty for `EXPOSE`.
  memory: z.array(DossierMemoryGroupSchema),
});

/** An Objekt's facts: the dossier's entries without memory and brief. */
export const PropertyFactsSchema = z.object({
  propertyId: refersTo('property', z.string()),
  entries: z.array(DossierEntrySchema),
  factsDetected: z.boolean(),
});

export type DossierAuthoritative = z.infer<typeof DossierAuthoritativeSchema>;
export type DossierAdvisoryEntry = z.infer<typeof DossierAdvisoryEntrySchema>;
export type DossierEntry = z.infer<typeof DossierEntrySchema>;
export type DossierFactValueType = z.infer<typeof DossierFactValueTypeSchema>;
export type DossierNumberFormat = z.infer<typeof DossierNumberFormatSchema>;
export type DossierMemoryEntry = z.infer<typeof DossierMemoryEntrySchema>;
export type DossierMemoryGroup = z.infer<typeof DossierMemoryGroupSchema>;
export type DossierBrief = z.infer<typeof DossierBriefSchema>;
export type PropertyDossier = z.infer<typeof PropertyDossierSchema>;
export type PropertyFacts = z.infer<typeof PropertyFactsSchema>;
